import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { useIssueColors, useIssueNavigation, useLoadingComponents } from '../hooks';
import { IssueCard } from './IssueCard';
import { getInterventionDate } from '../utils/interventionDate';

export default function InterventionPointIssues({ objectKey }) {
  const { t } = useTranslation();
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [collapsed, setCollapsed] = useState(true);

  const { getStatusColor } = useIssueColors();
  const { handleNavigateToIssue } = useIssueNavigation();
  const { ErrorDisplay, EmptyState } = useLoadingComponents();

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!objectKey) return;
      try {
        setLoading(true);
        setError(null);
        const res = await fetchWithAuth(`/issues/byInterventionPoint/${encodeURIComponent(objectKey)}`);
        const body = await res.json();
        // Align with /issues/bySectors shape used elsewhere
        const list = body?.issues || body?.data?.issues || body?.data || [];
        if (!Array.isArray(list)) {
          throw new Error('Unexpected issues payload');
        }
        if (!cancelled) setIssues(list);
      } catch (e) {
        if (!cancelled) setError(e?.message || t('Unknown error'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [objectKey]);

  const sortedIssues = useMemo(() => {
    return (issues || []).slice().sort((a, b) => {
      const aDate = new Date(a.fields?.updated || a.updated || a.created || 0).getTime();
      const bDate = new Date(b.fields?.updated || b.updated || b.created || 0).getTime();
      return bDate - aDate;
    });
  }, [issues]);

  return (
    <div className="rounded border border-gray-200">
      <div className="flex items-center justify-between border-b px-3 py-2">
        <div className="flex items-center gap-2">
          <div className="text-sm font-medium text-gray-700">{t('Requests history')}</div>
          <span className="inline-flex items-center rounded-full bg-gray-100 text-gray-800 text-[10px] px-2 py-0.5">
            {(!objectKey || loading) ? '--' : (sortedIssues?.length ?? 0)}
          </span>
        </div>
        <button
          type="button"
          onClick={() => setCollapsed(s => !s)}
          className="text-xs text-gray-600 hover:text-gray-900"
        >
          {collapsed ? t('Show') : t('Hide')}
        </button>
      </div>
      {!collapsed && (
        <div className="p-3">
          {!objectKey ? (
            <div className="text-sm text-gray-500">{t('No intervention point selected.')}</div>
          ) : loading ? (
            <div className="flex justify-center py-4">
              <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary"></div>
            </div>
          ) : error ? (
            <ErrorDisplay error={error} onRetry={null} />
          ) : !sortedIssues || sortedIssues.length === 0 ? (
            <EmptyState message={t('No ticket found')} description={t('No ticket linked to this point of sale.')} />
          ) : (
            <div className="space-y-2">
              {sortedIssues.map(issue => {
                const statusColor = issue.fields?.statusCategory?.colorName || getStatusColor(issue.fields?.status);
                return (
                  <IssueCard
                    key={issue.key}
                    issueKey={issue.key}
                    createdAt={issue.created}
                    dueDate={getInterventionDate(issue)}
                    status={issue.fields?.status?.name || issue.status}
                    statusColor={statusColor}
                    summary={issue.mappedFields?.title || issue.summary}
                    interventionPoint={issue.mappedFields?.intervention_point}
                    onClick={() => handleNavigateToIssue(issue.key, issue)}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


